import { z } from 'zod'

import { pruned } from '@/lib/filters'

/** One row per SKU, or one per ASIN listing (ASIN and seller SKU) — SKU view and ASIN view. */
export const BUSINESS_VIEW_GRAINS = ['sku', 'listing'] as const
export type BusinessViewGrain = (typeof BUSINESS_VIEW_GRAINS)[number]

export const BUSINESS_VIEW_PAGE_SIZES = [25, 50, 100] as const

/** VentaHub's filter operators, by what the column holds. */
export const TEXT_OPERATORS = [
  'contains',
  'equals',
  'startsWith',
  'endsWith',
  'isEmpty',
  'isNotEmpty',
  'isAnyOf',
  'isNoneOf',
] as const
export const NUMBER_OPERATORS = [
  'equals',
  '!=',
  '>',
  '>=',
  '<',
  '<=',
  'isEmpty',
  'isNotEmpty',
] as const
export type FilterOperator = (typeof TEXT_OPERATORS)[number] | (typeof NUMBER_OPERATORS)[number]

const OPERATORS = [...new Set<FilterOperator>([...TEXT_OPERATORS, ...NUMBER_OPERATORS])] as [
  FilterOperator,
  ...FilterOperator[],
]

/** The most conditions one view holds: the API's limit. */
export const MAX_FILTERS = 20

/** One condition of the Filters builder, as the API takes it. */
export const rowFilterSchema = z.object({
  field: z.string().regex(/^[a-z0-9_]+$/),
  operator: z.enum(OPERATORS),
  value: z.union([z.string(), z.array(z.string())]).optional(),
})
export type RowFilter = z.infer<typeof rowFilterSchema>

const sortKey = z.string().regex(/^[a-z0-9_]+:(asc|desc)$/)
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

/**
 * Business View's URL: the warehouses, the marketplace, the stores, the
 * brands and the range; the row grain; the builder's filters and sort keys;
 * whether the view has been generated; the page; and what is hidden or open.
 * Every field optional; defaults apply at read time.
 */
export const businessViewSearchSchema = z.object({
  warehouseIds: z
    .array(z.number().int().positive())
    .max(50)
    .optional()
    .catch(undefined)
    .describe(
      'Warehouses to read, by id; a warehouse stands in for its stores’ marketplaces. Changing it clears go.',
    ),
  storeIds: z
    .array(z.number().int().positive())
    .max(50)
    .optional()
    .catch(undefined)
    .describe(
      'Stores to include, by id; empty means every store of the marketplace. Changing it clears go.',
    ),
  marketplaceId: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe(
      'The marketplace to read, by id. Generating needs it or a warehouse. Changing it clears go.',
    ),
  dateFrom: day
    .optional()
    .catch(undefined)
    .describe('First day of the range, YYYY-MM-DD; the last 30 days when absent.'),
  dateTo: day
    .optional()
    .catch(undefined)
    .describe('Last day of the range, YYYY-MM-DD, inclusive; today when absent.'),
  brandIds: z
    .array(z.number().int())
    .optional()
    .catch(undefined)
    .describe('Brands to include, by id; empty means every brand.'),
  view: z
    .enum(BUSINESS_VIEW_GRAINS)
    .optional()
    .catch(undefined)
    .describe(
      'Row grain: sku is one row per SKU (SKU view), listing one per ASIN listing (ASIN view). Default sku.',
    ),
  // Text matched against the SKU code, the ASINs, the seller SKUs and the titles.
  q: z
    .string()
    .max(100)
    .optional()
    .catch(undefined)
    .describe('Search text matched against the SKU code, ASINs, seller SKUs and titles.'),
  // The header funnels' conditions, as JSON; the API takes the same JSON.
  filter: z
    .array(rowFilterSchema)
    .max(MAX_FILTERS)
    .optional()
    .catch(undefined)
    .describe('Row filter conditions, each {field, operator, value}, all of which must hold.'),
  sorts: z
    .array(sortKey)
    .max(5)
    .optional()
    .catch(undefined)
    .describe(
      'Sort keys as field:asc|desc, in priority; default total_amount:desc with a marketplace, else total_units:desc.',
    ),
  // Set by Generate: nothing loads until it is.
  go: z
    .literal(1)
    .optional()
    .catch(undefined)
    .describe('1 once Generate Business View is pressed; nothing loads without it.'),
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('Page of the grid, from 1; default 1.'),
  limit: z
    .union([z.literal(25), z.literal(50), z.literal(100)])
    .optional()
    .catch(undefined)
    .describe('Rows per page; default 100.'),
  // Column groups folded away.
  hide: z
    .array(z.string().regex(/^[a-zA-Z]+$/))
    .optional()
    .catch(undefined)
    .describe('Column groups folded away, by group id (salesQty, revenue, financial, stock, …).'),
  // The ASIN open in the product drawer, and whether it opened at its ad groups.
  detail: z
    .string()
    .max(40)
    .optional()
    .catch(undefined)
    .describe('The ASIN open in the Product Details drawer.'),
  adgroups: z
    .literal(1)
    .optional()
    .catch(undefined)
    .describe('1 opens the product drawer at its Marketing Adgroups list.'),
  // The store whose listing the product drawer shows, when several list the ASIN.
  detailStore: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The store whose listing the product drawer shows, when several list the ASIN.'),
  // The product drawer's Payments tab, and the page of its orders.
  detailTab: z
    .enum(['payments'])
    .optional()
    .catch(undefined)
    .describe('payments opens the drawer’s Payments tab; absent is Details.'),
  detailPage: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('Page of the drawer’s payments list; default 1.'),
})

export type BusinessViewSearch = z.infer<typeof businessViewSearchSchema>

export const EMPTY_BUSINESS_SEARCH: BusinessViewSearch = businessViewSearchSchema.parse({})

/** Reset: every field cleared, so the page is as it first opened. */
export const RESET_BUSINESS_VIEW: Partial<BusinessViewSearch> = Object.fromEntries(
  Object.keys(businessViewSearchSchema.shape).map((key) => [key, undefined]),
)

/** Opens by SKU, VentaHub's default sort, a hundred rows a page. */
export const DEFAULT_GRAIN: BusinessViewGrain = 'sku'
export const DEFAULT_PAGE_SIZE = 100

/** The Sort panel holds this many keys; the API takes five, so older links still read. */
export const MAX_SORT_KEYS = 3

/**
 * VentaHub's default order: Total $ highest first. The API breaks ties by
 * warehouse stock, then FBA stock, as VentaHub's second and third keys did.
 *
 * Amounts sort only within one currency, so a view with no marketplace —
 * a warehouse, whose stores may sell in several — opens by Total units.
 */
export function defaultSorts(search: Pick<BusinessViewSearch, 'marketplaceId'>): string[] {
  return search.marketplaceId === undefined ? ['total_units:desc'] : ['total_amount:desc']
}

/** Strip defaults and empties so a clean URL stays clean. */
export function withoutBusinessDefaults(search: BusinessViewSearch): BusinessViewSearch {
  const sorts = search.sorts
  const fallback = defaultSorts(search)
  const isDefault =
    sorts !== undefined &&
    sorts.length === fallback.length &&
    sorts.every((key, at) => key === fallback[at])
  return pruned({
    ...search,
    view: search.view === DEFAULT_GRAIN ? undefined : search.view,
    sorts: isDefault ? undefined : sorts,
    limit: search.limit === DEFAULT_PAGE_SIZE ? undefined : search.limit,
  })
}

/** Whether the view says where to read: a marketplace, or a warehouse standing in for its marketplaces. */
export function hasPlace(search: BusinessViewSearch): boolean {
  return search.marketplaceId !== undefined || (search.warehouseIds?.length ?? 0) > 0
}

/** A sort key as the URL and the API carry it. */
export function sortKeyOf(field: string, order: 'asc' | 'desc'): string {
  return `${field}:${order}`
}

/** A sort key, read back. */
export function parseSortKey(key: string): { field: string; order: 'asc' | 'desc' } {
  const [field = '', order] = key.split(':')
  return { field, order: order === 'asc' ? 'asc' : 'desc' }
}
